/**
 * Constants for the Cashflow Management screen. All data is fetched live
 * from the treasury GL endpoints; this file only holds enums, display
 * metadata, and rendering scaffolds shared across the screen's components.
 */
import { SCOPES } from '../../constants/scopes.constants';

export const GL_TYPE = Object.freeze({
    ADVANCE: 'advanceGL',
    FEE: 'feeGL',
});

// URL search-param keys. Centralised here so the controller, the
// `<Page>` wrapper, and any external link that wants to deep-link into
// a specific tab / period agree on the same names.
export const TAB_SEARCH_PARAM_KEY = 'tab';
export const TIMEFRAME_SEARCH_PARAM_KEY = 'period';
export const DATE_RANGE_SEARCH_PARAM_KEY = 'range';
export const TREASURY_DEFAULT_TIMEFRAME = '3m';

export {
    DATE_RANGE_PARAM_FORMAT,
    DEFAULT_TIMEFRAME,
    VALID_TIMEFRAMES,
} from '../../utils/periodParams';

/** `requiredScope` is checked in addition to SCOPES.TREASURY_VIEW (the
 * Page-level gate) — see treasury.controller.js#getAccessibleTabs. */
export const TABS = [
    { key: GL_TYPE.ADVANCE, label: 'Advance GL Account', requiredScope: SCOPES.TREASURY_ADVANCE_VIEW },
    { key: GL_TYPE.FEE, label: 'Fee GL Account', requiredScope: SCOPES.TREASURY_FEE_VIEW },
];

export const GL_METRICS_LABEL = Object.freeze({
    [GL_TYPE.ADVANCE]: 'ADVANCE GL METRICS',
    [GL_TYPE.FEE]: 'FEE GL METRICS',
});

export const GL_SECTION = Object.freeze({
    ACCOUNT: 'account',
    FUNDING: 'funding',
    KPIS: 'kpis',
    CASHFLOW: 'cashflow',
    AGING: 'aging',
});

export const CASHFLOW_GROUPING = Object.freeze({
    QUARTERLY: 'quarterly',
    MONTHLY: 'monthly',
});

export const FUNDING_STATUS = Object.freeze({
    HEALTHY: 'healthy',
    AT_RISK: 'at_risk',
    DEPOSIT_NEEDED: 'deposit_needed',
});

export const TRANSACTION_TYPE = Object.freeze({
    INSTANT: 'instant',
    STANDARD: 'standard',
});

export const TRANSACTION_STATUS = Object.freeze({
    COMPLETE: 'complete',
    PENDING: 'pending',
    FAILED: 'failed',
});

// Aging buckets shown in the Aging Receivables card. Order is significant —
// renderer iterates this array.
export const AGING_BUCKETS = [
    { key: '1-14', label: '1 - 14 Days' },
    { key: '15-30', label: '15 - 30 Days' },
    { key: '31-60', label: '31 - 60 Days' },
    { key: '61-90', label: '61 - 90 Days' },
    { key: '91-180', label: '91 - 180 Days' },
];

// Slices shown by both the donut chart and the legend rendered inline in
// `AccountBalanceCard`. Keeping the colour and label co-located here means
// the chart wedge and the legend swatch can never drift. Each entry maps a
// `key` from the API breakdown payload (`{ advanced, overdue, available }`)
// to its display label and colour. Order is significant — the donut renders
// slices in this order and the legend lists them in the same order.
export const ACCOUNT_BREAKDOWN_ITEMS = [
    { key: 'advanced', label: 'Advanced funds', color: '#0059c5' },
    { key: 'overdue', label: 'Overdue funds', color: '#ef4444' },
    { key: 'available', label: 'Available funds', color: '#e2e8f0' },
];

// Inflow / outflow series rendered by the cashflow chart. Both `CashflowBars`
// and the inline `Legend` inside `CashflowChartCard` derive their items from
// this array, so the bar fill and the legend swatch can never drift — same
// pattern as `ACCOUNT_BREAKDOWN_ITEMS`. Each entry maps a `key` from the
// controller payload (`{ bucket, inflow, outflow }`) to its display label
// and colour. Order is significant — bars are flattened in this order and
// the legend lists them in the same order.
export const CASHFLOW_SERIES = [
    { key: 'inflow', label: 'Inflows', color: '#0059c5' },
    { key: 'outflow', label: 'Outflows', color: 'rgba(0, 89, 197, 0.2)' },
];

export const DEFAULT_PAGE_SIZE = 10;

export const PAGE_METADATA = {
    title: { text: 'Cashflow', highlight: 'Management' },
    description:
        'Monitor and reconcile your Early Pay general ledger accounts in real time, including funds advanced, outstanding balances, overdue amounts, and key financial health metrics.',
};
